// Centralised API client for the React app.
// Why: prior to this, every page used raw axios with inconsistent error
// handling, no retries, and manual auth header attachment. This wrapper
// adds:
//   1. Automatic Supabase JWT attachment (no more per-call getHeaders())
//   2. 2-retry exponential backoff on network failures and 5xx errors
//   3. A 15-second per-request timeout
//
// New code should import from here instead of raw axios. Existing pages
// migrate gradually — see Section 8 of the hardening plan.
import axios from 'axios'
import { supabase } from './supabase'
import { dispatchAuthError } from './authErrorHandlers'
import { refreshSessionOnce, tryRefreshAndReplay } from './sessionRefresh'

const api = axios.create({ timeout: 15000 })

// Mirror the auth chokepoint from AuthContext.jsx onto this instance —
// axios.create() does not inherit interceptors from the default singleton.
// Both sites delegate to dispatchAuthError in authErrorHandlers.js, the
// single source of truth for (status, code) → handler routing.
//
// Interceptor ordering on `api` matters for AUTH_TRANSIENT UX:
//   - this chokepoint is registered FIRST → fires LAST (axios response
//     interceptors fire in reverse-registration order)
//   - the retry interceptor below is registered THIRD → fires FIRST
// So on AUTH_TRANSIENT (503): retry exhausts up to 2× with backoff
// before this chokepoint sees the error → toast on final failure. On the
// default axios singleton (AuthContext.jsx) there's no retry interceptor,
// so the chokepoint sees the first 503 immediately → toast right away.
// Both paths converge on handleAuthTransient (toast + no destructive
// action) — different latency, same UX outcome. Documented intentionally;
// do not "fix" the ordering without understanding this comment.
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    // Per-request opt-out for fire-and-forget calls (log-ip ping, deferred
    // signup writes) where an auth failure must not drive navigation,
    // signOut, or toasts — the caller's .catch() owns the failure. Honored
    // ONLY on this instance; the raw-axios singleton chokepoint in
    // AuthContext.jsx ignores the flag. The error is still re-rejected.
    if (err?.config?.skipAuthDispatch) return Promise.reject(err)
    // Stale-token recovery: on 401 AUTH_INVALID, refresh once and replay once
    // before any logout (single-flight + single-retry — see sessionRefresh.js).
    // Returns the replayed response on success; null when not eligible or the
    // refresh failed, in which case we fall through to the existing dispatch.
    const replayed = await tryRefreshAndReplay(err, api)
    if (replayed) return replayed
    dispatchAuthError(err?.response?.status, err?.response?.data?.code, err?.response?.data)
    return Promise.reject(err)
  }
)

// Auth interceptor — attach Supabase JWT to every request
api.interceptors.request.use(async (config) => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (session) {
      config.headers = config.headers ?? {}
      config.headers.Authorization = `Bearer ${session.access_token}`
    } else if (!config.skipAuthDispatch) {
      // No session from getSession() (e.g. the SDK's background auto-refresh
      // was throttled on a backgrounded tab). Attempt ONE refresh before
      // firing bare — bounded (single-flight, never a wait-loop). Skipped for
      // fire-and-forget bootstrap pings so their timing is unchanged. If the
      // refresh yields nothing, proceed unauthenticated exactly as before
      // (terminal NO_AUTH — no "Bearer null", 2026-05-13 regression class).
      const token = await refreshSessionOnce()
      if (token) {
        config.headers = config.headers ?? {}
        config.headers.Authorization = `Bearer ${token}`
      }
    }
  } catch {
    // If we can't get a session, send the request unauthenticated.
    // The server will return 401 and the UI can react.
  }
  return config
})

// Retry interceptor — exponential backoff on network errors and 5xx
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err.config
    if (!config || config._retryCount >= 2) return Promise.reject(err)
    const status = err.response?.status
    // Retry only on network errors or 5xx — not 4xx (those are client errors)
    const shouldRetry = !err.response || (status >= 500 && status < 600)
    if (!shouldRetry) return Promise.reject(err)
    config._retryCount = (config._retryCount ?? 0) + 1
    // Backoff: 500ms, then 1000ms
    await new Promise(r => setTimeout(r, 500 * Math.pow(2, config._retryCount - 1)))
    return api(config)
  }
)

export default api
